// The orientation gnomon, as the component sheet (design/system/sheet.html, HUD) draws it, and the
// camera axes it is drawn from.

import type { Preset, Vec3 } from '../viewport/api.ts';

/** The view each letter gives when clicked (spec §10). */
export const LETTER_VIEW: Record<string, Preset> = { R: 'right', L: 'left', A: 'anterior', P: 'posterior', S: 'superior', I: 'inferior' };

/** The camera's right, up and toward-the-viewer axes in RAS, from a view-projection matrix
 *  (column-major). Clip w is minus the view depth, so its row is the viewing direction; the x and y
 *  rows are the right and up axes plus whatever lens shift adds along that direction. */
export function cameraAxes(m: ArrayLike<number>): { r: Vec3; u: Vec3; d: Vec3 } {
  const row = (i: number): Vec3 => [m[i]!, m[4 + i]!, m[8 + i]!];
  const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const unit = (a: Vec3): Vec3 => {
    const l = Math.hypot(a[0], a[1], a[2]) || 1;
    return [a[0] / l, a[1] / l, a[2] / l];
  };
  const w = row(3);
  const d = unit([-w[0], -w[1], -w[2]]);
  const flat = (a: Vec3): Vec3 => {
    const k = dot(a, d);
    return unit([a[0] - k * d[0], a[1] - k * d[1], a[2] - k * d[2]]);
  };
  return { r: flat(row(0)), u: flat(row(1)), d };
}

const AXES: [string, string, Vec3][] = [
  ['R', 'L', [1, 0, 0]],
  ['A', 'P', [0, 1, 0]],
  ['S', 'I', [0, 0, 1]],
];

/** The gnomon's SVG (spec §10, drawn as the component sheet draws it): R, A and S as the camera sees
 *  them at their true foreshortening, the axis nearest the viewer drawn white, and L at the far end
 *  of the lateral axis at 55 % (P and I would only crowd the circle). Each letter sits half a letter
 *  past its line's end. An axis seen end-on collapses to the letter facing the camera, at the
 *  centre, and the other lines start clear of it. */
export function gnomonSvg(axes: { r: Vec3; u: Vec3; d: Vec3 }, size: number, letterSize = 10): string {
  const R = size / 2;
  const gap = letterSize / 2; // between a line's end and its letter's centre
  const reach = R - 8; // a letter's centre for an axis across the picture: 24 in the 64 px circle
  const full = reach - gap; // that axis's line: 19
  const hub = gap + 2; // where lines start when a letter sits at the centre: 7
  const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const items = AXES.map(([pos, neg, v]) => ({ pos, neg, sx: dot(axes.r, v), sy: -dot(axes.u, v), toward: dot(axes.d, v) }));
  const nearest = items.reduce((a, b) => (b.toward > a.toward ? b : a));
  const near = nearest.toward > 0.2 && Math.hypot(nearest.sx, nearest.sy) >= 0.2 ? nearest : null;
  const collapsed = items.find((it) => Math.hypot(it.sx, it.sy) < 0.2);
  const f = (n: number) => n.toFixed(1);
  const text = (x: number, y: number, t: string, far: boolean) => `<text x="${f(x)}" y="${f(y)}" data-view="${LETTER_VIEW[t]}"${far ? ' class="far"' : ''}>${t}</text>`;
  let lines = '';
  let letters = '';
  // Far lines first, so the nearest is drawn over the others at the centre; letters over every line.
  for (const it of [...items].sort((a, b) => a.toward - b.toward)) {
    if (it === collapsed) continue;
    const len = Math.hypot(it.sx, it.sy);
    const ux = it.sx / len;
    const uy = it.sy / len;
    const start = collapsed ? hub : 0;
    const end = full * len;
    lines += `<line${it === near ? ' class="near"' : ''} x1="${f(ux * start)}" y1="${f(uy * start)}" x2="${f(ux * end)}" y2="${f(uy * end)}"/>`;
    letters += text(ux * (end + gap), uy * (end + gap), it.pos, false);
    if (it.pos === 'R') letters += text(-ux * (end + gap), -uy * (end + gap), it.neg, true);
  }
  if (collapsed) letters += text(0, 0, collapsed.toward > 0 ? collapsed.pos : collapsed.neg, false);
  return `<circle class="orientation-bg" r="${R - 0.5}"/>${lines}${letters}`;
}
